import { __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useMemo, useCallback, useRef, useState, useEffect, } from 'react';
import classNames from 'classnames';
import DIcon from '../DIcon';
const DButton = forwardRef((props, ref) => {
    const { color = 'primary', size, variant, text, children, iconStart, iconStartFamilyClass, iconStartFamilyPrefix, iconStartMaterialStyle, iconEnd, iconEndFamilyClass, iconEndFamilyPrefix, iconEndMaterialStyle, loading = false, loadingText, loadingAriaLabel, disabled = false, className, style, dataAttributes, onClick, type = 'button', target, rel } = props, rest = __rest(props, ["color", "size", "variant", "text", "children", "iconStart", "iconStartFamilyClass", "iconStartFamilyPrefix", "iconStartMaterialStyle", "iconEnd", "iconEndFamilyClass", "iconEndFamilyPrefix", "iconEndMaterialStyle", "loading", "loadingText", "loadingAriaLabel", "disabled", "className", "style", "dataAttributes", "onClick", "type", "target", "rel"]);
    const [buttonWidth, setButtonWidth] = useState();
    const buttonRef = useRef(null);
    const isDisabled = useMemo(() => disabled || loading, [disabled, loading]);
    const content = useMemo(() => children || text, [children, text]);
    const classes = useMemo(() => {
        const variantClass = variant
            ? `btn-${variant}-${color}`
            : `btn-${color}`;
        return {
            btn: true,
            [variantClass]: true,
            [`btn-${size}`]: !!size,
            loading,
        };
    }, [variant, color, size, loading]);
    const ariaLabel = useMemo(() => {
        const ariaLabelProp = rest['aria-label'];
        return loading
            ? loadingAriaLabel || ariaLabelProp || text
            : ariaLabelProp || text;
    }, [loading, loadingAriaLabel, rest, text]);
    const handleClick = useCallback((event) => {
        if (disabled || loading) {
            event.preventDefault();
            return;
        }
        onClick === null || onClick === void 0 ? void 0 : onClick(event);
    }, [disabled, loading, onClick]);
    useEffect(() => {
        if (!loading && buttonRef.current) {
            const width = buttonRef.current.offsetWidth;
            if (width > 0)
                setButtonWidth(width);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [content, iconEnd, iconStart]);
    if (props.href) {
        return (_jsxs("a", Object.assign({ href: props.href, target: target, rel: rel, ref: (node) => {
                buttonRef.current = node;
                if (typeof ref === 'function')
                    ref(node);
                // eslint-disable-next-line max-len
                // eslint-disable-next-line no-param-reassign, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
                else if (ref)
                    ref.current = node;
            }, className: classNames(classes, className), style: Object.assign(Object.assign({}, style), (loading && buttonWidth
                ? { minWidth: `${buttonWidth}px` }
                : undefined)), "aria-label": ariaLabel, "aria-busy": loading, "aria-disabled": isDisabled, onClick: handleClick }, dataAttributes, { children: [loading && (_jsxs("span", { className: "btn-loading", children: [_jsx("span", { className: "spinner-border spinner-border-sm", "aria-hidden": "true" }), loadingText && _jsx("span", { role: "status", children: loadingText })] })), !loading && (_jsxs(_Fragment, { children: [iconStart && (_jsx(DIcon, { icon: iconStart, familyClass: iconStartFamilyClass, familyPrefix: iconStartFamilyPrefix, materialStyle: iconStartMaterialStyle })), content, iconEnd && (_jsx(DIcon, { icon: iconEnd, familyClass: iconEndFamilyClass, familyPrefix: iconEndFamilyPrefix, materialStyle: iconEndMaterialStyle }))] }))] })));
    }
    return (_jsxs("button", Object.assign({ ref: (node) => {
            buttonRef.current = node;
            if (typeof ref === 'function')
                ref(node);
            // eslint-disable-next-line max-len
            // eslint-disable-next-line no-param-reassign, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
            else if (ref)
                ref.current = node;
        }, 
        // eslint-disable-next-line react/button-has-type
        type: type, className: classNames(classes, className), style: Object.assign(Object.assign({}, style), (loading && buttonWidth
            ? { minWidth: `${buttonWidth}px` }
            : undefined)), disabled: isDisabled, "aria-label": ariaLabel, "aria-busy": loading, "aria-disabled": isDisabled, onClick: handleClick }, dataAttributes, rest, { children: [loading && (_jsxs("span", { className: "btn-loading", children: [_jsx("span", { className: "spinner-border spinner-border-sm", "aria-hidden": "true" }), loadingText && _jsx("span", { role: "status", children: loadingText })] })), !loading && (_jsxs(_Fragment, { children: [iconStart && (_jsx(DIcon, { icon: iconStart, familyClass: iconStartFamilyClass, familyPrefix: iconStartFamilyPrefix, materialStyle: iconStartMaterialStyle })), content, iconEnd && (_jsx(DIcon, { icon: iconEnd, familyClass: iconEndFamilyClass, familyPrefix: iconEndFamilyPrefix, materialStyle: iconEndMaterialStyle }))] }))] })));
});
DButton.displayName = 'DButton';
export default DButton;
//# sourceMappingURL=DButton.js.map